import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';

import {
  ArrowsClockwise,
  Check,
  CircleNotch,
  Envelope,
  PencilSimple,
  Plus,
  Shield,
  ShieldCheck,
  Trash,
  UsersThree,
  WarningCircle,
  X
} from '@phosphor-icons/react';
const AdminUsersManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchCurrentAdmin = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/me`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentAdmin(data);
      }
    } catch (error) {
      console.error('Error fetching current admin:', error);
    }
  };

  const fetchAdmins = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/admins`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 403) {
        toast.error('Only master admin can access this page');
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch admin users');
      }
      
      const data = await response.json();
      setAdmins(data.admins || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load admin users');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentAdmin();
    fetchAdmins();
  }, []);

  const openCreateModal = () => {
    setFormData({ email: '', password: '', name: '' });
    setModalMode('create');
    setSelectedAdmin(null);
    setShowModal(true);
  };

  const openEditModal = (admin) => {
    setFormData({ 
      email: admin.email || admin.username, 
      password: '', 
      name: admin.name || '' 
    });
    setModalMode('edit');
    setSelectedAdmin(admin);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (modalMode === 'create') {
        if (!formData.email || !formData.password) {
          toast.error('Email and password are required');
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/admin/admins`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(formData)
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || 'Failed to create admin');
        }

        toast.success('Admin user created successfully');
      } else {
        // PencilSimple mode
        const updateData = { name: formData.name };
        if (formData.email !== (selectedAdmin.email || selectedAdmin.username)) {
          updateData.email = formData.email;
        }
        if (formData.password) {
          updateData.password = formData.password;
        }

        const response = await fetch(`${BACKEND_URL}/api/admin/admins/${selectedAdmin.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(updateData)
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || 'Failed to update admin');
        }

        toast.success('Admin user updated successfully');
      }

      setShowModal(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (admin) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/admins/${admin.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to delete admin');
      }

      toast.success('Admin user deleted successfully');
      setDeleteConfirm(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (!currentAdmin?.is_master) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
          <WarningCircle className="w-5 h-5 text-amber-400" />
          <p className="text-amber-400">Only the master admin can manage admin users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <UsersThree className="w-7 h-7 text-[#6366F1]" />
            Admin UsersThree
          </h1>
          <p className="text-[#A0AEC0] mt-1">Manage platform administrators</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={fetchAdmins}
            className="flex items-center gap-2 px-4 py-2 rounded-lg glass text-[#A0AEC0] hover:text-white transition-colors"
          >
            <ArrowsClockwise className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            ArrowsClockwise
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="w-4 h-4" />
            Add Admin
          </button>
        </div>
      </div>

      {/* Admin List */}
      <div className="glass rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <CircleNotch className="w-8 h-8 text-[#6366F1] animate-spin" />
          </div>
        ) : admins.length === 0 ? (
          <div className="text-center py-12">
            <UsersThree className="w-12 h-12 text-[#718096] mx-auto mb-4" />
            <p className="text-[#A0AEC0]">No admin users found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-6 py-4 text-sm font-medium text-[#A0AEC0]">Email</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#A0AEC0]">Name</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#A0AEC0]">Role</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-[#A0AEC0]">Created</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-[#A0AEC0]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.id || admin._id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                        <Envelope className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-white">{admin.email || admin.username}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[#A0AEC0]">
                    {admin.name || '-'}
                  </td>
                  <td className="px-6 py-4">
                    {admin.is_master ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm">
                        <ShieldCheck className="w-4 h-4" />
                        Master Admin
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#6366F1]/20 text-[#818CF8] text-sm">
                        <Shield className="w-4 h-4" />
                        Admin
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-[#A0AEC0]">
                    {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditModal(admin)}
                        className="p-2 rounded-lg hover:bg-white/10 text-[#A0AEC0] hover:text-white transition-colors"
                        title="Edit"
                      >
                        <PencilSimple className="w-4 h-4" />
                      </button>
                      {!admin.is_master && (
                        <>
                          {deleteConfirm === admin.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(admin)}
                                className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                title="Confirm Delete"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="p-2 rounded-lg hover:bg-white/10 text-[#A0AEC0] transition-colors"
                                title="Cancel"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(admin.id)}
                              className="p-2 rounded-lg hover:bg-red-500/20 text-[#A0AEC0] hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <Trash className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/PencilSimple Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {modalMode === 'create' ? 'Add New Admin' : 'Edit Admin'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-lg hover:bg-white/10 text-[#A0AEC0] hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors placeholder-[#718096]"
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                  Name (Optional)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors placeholder-[#718096]"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                  Password {modalMode === 'edit' && <span className="text-[#718096]">(leave blank to keep current)</span>}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={modalMode === 'create'}
                  minLength={8}
                  className="w-full px-4 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors placeholder-[#718096]"
                  placeholder={modalMode === 'edit' ? '••••••••' : 'Minimum 8 characters'}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 rounded-xl glass text-[#A0AEC0] hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting && <CircleNotch className="w-4 h-4 animate-spin" />}
                  {modalMode === 'create' ? 'Create Admin' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersManagement;
